'use client';

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TriviaSectionDesktop,
  TriviaSectionMobile,
} from '../trivia/TriviaSection';

import { CountdownDesktop } from './components/Countdown';
import { GeneralTournament } from './components/GeneralTournament';
import { NewsSection } from './components/NewsSection';
import { PinsSection } from './components/PinsSection';
import { PopupImageVideo } from './components/PopupImageVIdeo';
import { RNKFecha } from './components/RNKFecha';
import { SurveySection } from './components/SurveySection';
import TeamSection from './components/TeamSection';
import { useModalChecks } from './hooks/useModalCheck';

import { Container, Divider, NavBar } from '@/components';
import { BoxGoPremium } from '@/components/BoxGoPremium';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import useAuth from '@/hooks/useAuth';
import useCheckPublicacion from '@/hooks/useCheckPublicacion';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import { usePermutive } from '@/hooks/usePermutive';
import { usePopupImageVideo } from '@/hooks/usePopupImageVideo';
import ROUTES from '@/router/routes';
import { InvitationType } from '@/types/invites';
import { EnumContextGDT } from '@/types/team';
import { letterGenderById } from '@/utils';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';

const DesktopBody = () => {
  const navigate = useNavigate();
  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.home });
  useMetaData({
    title: 'Inicio',
  });

  useEffect(() => {
    const invitationId = localStorage.getItem('invitationId');
    const invitationType = localStorage.getItem('invitationType');

    if (invitationId && invitationType) {
      if (invitationType === InvitationType.COPA) {
        navigate(ROUTES.friendsCup.home);
      } else if (invitationType === InvitationType.TORNEO) {
        navigate(ROUTES.friendsTournament);
      } else if (invitationType === InvitationType.MINILIGA) {
        navigate(ROUTES.minileagues.home);
      }
    }
  }, [navigate]);

  return (
    <>
      {/* banner horizontal */}
      <AdSlot
        className="mt-4"
        divId={ADS_DIV_ID.horizontal}
        variant="HORIZONTAL"
      />
      <Container variant="fluid">
        <div className="flex justify-center mt-5 container mx-auto">
          <div className="w-full flex justify-center ">
            <CountdownDesktop />
          </div>
        </div>
        <div className="grid grid-cols-3 mt-7 container mx-auto gap-5">
          <div className="col-span-2 flex gap-x-3 w-full h-full">
            <div className="w-full flex-1 flex flex-col">
              <RNKFecha />
            </div>
            <div className="w-full flex-1 flex flex-col">
              <GeneralTournament />
            </div>
          </div>
          <div className="col-span-1">
            <TeamSection />
          </div>
          <div className="col-span-2 flex gap-x-3 w-full ">
            <NewsSection />
          </div>
          <div className="w-full col-span-1">
            <SurveySection />
          </div>
          <div className="col-span-2 flex gap-x-3 w-full ">
            <TriviaSectionDesktop />
          </div>
          <div className="w-full col-span-1">
            <BoxGoPremium
              className="h-[414px]"
              context={EnumContextGDT.Summary}
              showStar={true}
            />
          </div>
        </div>
        {/* box banner */}
        <div className="justify-center flex lg:space-x-[99px] lg:mb-12 lg:mt-14 my-6">
          <AdSlot divId={ADS_DIV_ID.caja1} variant="BOX" />
          <AdSlot divId={ADS_DIV_ID.caja2} variant="BOX" />
          <AdSlot divId={ADS_DIV_ID.caja3} variant="BOX" />
        </div>
      </Container>
    </>
  );
};

const MobileBody = () => {
  const navigate = useNavigate();
  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.home });

  useEffect(() => {
    const invitationType = localStorage.getItem('invitationType');

    if (invitationType === InvitationType.COPA) {
      navigate(ROUTES.friendsCup.home);
    } else if (invitationType === InvitationType.TORNEO) {
      navigate(ROUTES.friendsTournament);
    } else if (invitationType === InvitationType.MINILIGA) {
      navigate(ROUTES.minileagues.home);
    }
  }, [navigate]);

  return (
    <>
      <div className="flex justify-center w-full">
        <div className=" w-full flex justify-center ">
          <CountdownDesktop />
        </div>
      </div>
      <div className="flex justify-center mx-5 mt-3">
        {/* banner horizontal */}
        <AdSlot
          className="flex mx-auto justify-center mt-4"
          divId={ADS_DIV_ID.horizontal}
          variant="HORIZONTAL"
        />
      </div>
      <div className="flex flex-col mx-5 gap-7 mt-7">
        <div className=" w-full flex ">
          <RNKFecha />
        </div>
        <div className="w-full flex">
          <GeneralTournament />
        </div>
        <Divider type="horizontal" />
        <PinsSection />
        <NewsSection />
        <TriviaSectionMobile isInDashboard={true} />
        <SurveySection />
        {/* footer banner */}
        <div className="flex justify-center">
          <AdSlot
            className="mx-auto flex justify-center mb-8"
            divId={ADS_DIV_ID.caja1}
            variant="BOX"
          />
        </div>
      </div>
    </>
  );
};

export const Summary = () => {
  const { isDesktop } = useIsDesktop();
  const { userInitialData } = useAuth();
  useCheckPublicacion('A');
  const { ModalDateF5, PremiumWinnerModal } = useModalChecks(
    userInitialData || {}
  );

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.home,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  const dataPP2 = usePopupImageVideo(
    (userInitialData) => userInitialData?.popups_resp[0]
  );

  let popupMarkup = <></>;
  if (dataPP2) {
    popupMarkup = <PopupImageVideo popup={dataPP2} />;
  }

  return (
    <NavBar
      desktop
      mobile={{
        leftContent: {
          showMenuAction: true,
          items: true,
        },
        centerContent: {
          displayLogo: true,
        },
        rightContent: {
          notificationsMenuAction: true,
        },
      }}
    >
      {popupMarkup}
      {isDesktop ? <DesktopBody /> : <MobileBody />}
      {/* Modales de ganadores se renderizan pero solo se muestran cuando es necesario */}
      <ModalDateF5 />,
      <PremiumWinnerModal />
    </NavBar>
  );
};
